import { useCurrentWorkspace } from '@/context/current-workspace';
import { hydraUiApi } from '@/services/api';
import { authStore } from '@/services/auth/store';
import { useCallback, useMemo } from 'react';
import { postCatalogEvent } from './event-name';
import { type IEvents, type EventProperties } from './types';
import { useIsAnalyticsEnabled } from './use-is-analytics-enabled';

export { setEventInterceptor, type EventInterceptor, type InterceptedEvent } from './event-interceptor';
export { trackEventForAccount, useAccountAnalytics } from './track-account-event';
export { useIsAnalyticsEnabled } from './use-is-analytics-enabled';

/**
 * Post a typed analytics event to the environment-scoped tracking endpoint
 * given the environment id directly, with no React context. `useAnalytics` reads
 * the environment from the workspace context, but callers mounted outside that
 * context (e.g. the reauth modal, which the modal-manager renders at the app
 * shell) can't — they thread the last-good environment id in and call this.
 * Best-effort: resolves on success or failure so it never blocks or surfaces.
 */
export function trackEventForEnvironment<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace] & string,
  Properties extends Record<EventProperties<Namespace, E>, unknown>,
>(environmentId: string, namespace: Namespace, event: E, properties: Properties): Promise<void> {
  return postCatalogEvent(namespace, event, properties, (body) =>
    hydraUiApi.tracking.createTrackEventForEnvironment({ params: { path: { environment_id: environmentId } }, body }),
  );
}

function sendToEnvironment<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace] & string,
  Properties extends Record<EventProperties<Namespace, E>, unknown>,
>(environmentId: string | null, namespace: Namespace, event: E, properties: Properties): Promise<void> {
  return postCatalogEvent(
    namespace,
    event,
    properties,
    environmentId == null
      ? null
      : (body) =>
          hydraUiApi.tracking.createTrackEventForEnvironment({
            params: { path: { environment_id: environmentId } },
            body,
          }),
  );
}

/** A workspace id from the URL: `/workspaces/:id`, or `?workspaceId=` in the embedded editor. Null for `last` or none. */
function getUrlEnvironmentId(): string | null {
  const id = getPathEnvironmentId() ?? new URLSearchParams(window.location.search).get('workspaceId');
  return id != null && Number(id) > 0 ? id : null;
}

function getPathEnvironmentId(): string | null {
  return window.location.pathname.match(/\/workspaces\/([^/]+)/)?.[1] ?? null;
}

/**
 * The workspace an embedded editor was opened for (`?workspaceId=`), when its path names none. The router's
 * current workspace only reads the path, so in an embed it is the user's last or first workspace instead.
 */
function getEmbedEnvironmentId(): string | null {
  return getPathEnvironmentId() == null ? getUrlEnvironmentId() : null;
}

/**
 * `useAnalytics().track` for code outside React (stores, plain files). It reads the masquerade state and the
 * workspace from the URL when called, so it needs no provider. Same event API and rules as the hook.
 */
export function track<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace] & string,
  // oxlint-disable-next-line local-rules/no-unsafe-dictionary-type -- callers pass whole domain objects; the catalog entry picks what is sent
  Properties extends Record<EventProperties<Namespace, E>, unknown>,
>(namespace: Namespace, event: E, properties: Properties): Promise<void> {
  if (authStore.get().account?.is_masq === true) {
    return Promise.resolve();
  }
  return sendToEnvironment(getUrlEnvironmentId(), namespace, event, properties);
}

export function useAnalytics() {
  const workspaceId = useCurrentWorkspace()?.id;
  const isEnabled = useIsAnalyticsEnabled();

  // Depends on the id, not the workspace object: some workspaces are rebuilt every render, and callers put
  // `track` in effect dependencies, where a new function each render would refire the effect.
  const track = useCallback(
    <
      Namespace extends keyof IEvents,
      E extends keyof IEvents[Namespace] & string,
      // oxlint-disable-next-line local-rules/no-unsafe-dictionary-type -- callers pass whole domain objects; the catalog entry picks what is sent
      Properties extends Record<EventProperties<Namespace, E>, unknown>,
    >(
      namespace: Namespace,
      event: E,
      properties: Properties,
    ) => {
      if (!isEnabled) {
        return Promise.resolve();
      }
      const environmentId =
        getEmbedEnvironmentId() ?? (workspaceId == null ? getUrlEnvironmentId() : String(workspaceId));
      return sendToEnvironment(environmentId, namespace, event, properties);
    },
    [isEnabled, workspaceId],
  );

  return useMemo(() => ({ track, isEnabled }), [track, isEnabled]);
}
